import React, { useEffect, useCallback, useState } from 'react';
import DailyIframe from '@daily-co/daily-js';
import { Mic, MicOff, Camera, CameraOff, Monitor, PhoneOff, Circle } from 'lucide-react';

const VideoCallRoom = ({ roomUrl, token, userName, onLeave, onRecordingReady }) => {
  const [callFrame, setCallFrame] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isCamOff, setIsCamOff] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [callState, setCallState] = useState('joining'); // joining, joined, left

  useEffect(() => {
    if (!roomUrl) return;

    const frame = DailyIframe.createCallObject({
      url: roomUrl,
      token: token,
      userName: userName || 'Participant',
    });

    frame.on('joined-meeting', () => {
      setCallState('joined');
      updateParticipants(frame);
    });

    frame.on('participant-joined', () => updateParticipants(frame));
    frame.on('participant-left', () => updateParticipants(frame));
    frame.on('participant-updated', () => updateParticipants(frame));

    frame.on('recording-started', () => setIsRecording(true));
    frame.on('recording-stopped', () => setIsRecording(false));

    frame.on('left-meeting', () => {
      setCallState('left');
      if (onLeave) onLeave();
    });

    frame.join({ url: roomUrl, token });
    setCallFrame(frame);

    return () => {
      frame.destroy();
    };
  }, [roomUrl]);

  const updateParticipants = (frame) => {
    const p = frame.participants();
    setParticipants(Object.values(p));
  };

  const toggleMic = useCallback(() => {
    if (!callFrame) return;
    callFrame.setLocalAudio(!isMuted ? false : true);
    setIsMuted(!isMuted);
  }, [callFrame, isMuted]);

  const toggleCam = useCallback(() => {
    if (!callFrame) return;
    callFrame.setLocalVideo(!isCamOff ? false : true);
    setIsCamOff(!isCamOff);
  }, [callFrame, isCamOff]);

  const toggleScreenShare = useCallback(() => {
    if (!callFrame) return;
    callFrame.startScreenShare();
  }, [callFrame]);

  const startRecording = useCallback(() => {
    if (!callFrame) return;
    callFrame.startRecording();
  }, [callFrame]);

  const stopRecording = useCallback(() => {
    if (!callFrame) return;
    callFrame.stopRecording();
  }, [callFrame]);

  const leaveCall = useCallback(() => {
    if (!callFrame) return;
    if (isRecording) callFrame.stopRecording();
    callFrame.leave();
  }, [callFrame, isRecording]);

  if (callState === 'left') {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-gray-900 rounded-xl p-8">
        <p className="text-white text-lg mb-4">Call ended</p>
        <p className="text-gray-400 text-sm">Recording is being processed...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-900 rounded-xl overflow-hidden" data-testid="video-call-room">
      {/* Video Grid */}
      <div className="flex-1 relative p-4">
        {callState === 'joining' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-white text-center">
              <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
              <p>Joining call...</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full">
          {participants.map((p, i) => (
            <div key={p.session_id || i} className="relative bg-gray-800 rounded-lg overflow-hidden flex items-center justify-center min-h-[200px]">
              {p.video ? (
                <video
                  autoPlay
                  playsInline
                  muted={p.local}
                  ref={(el) => {
                    if (el && p.videoTrack) {
                      el.srcObject = new MediaStream([p.videoTrack]);
                    }
                  }}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-xl font-bold">
                    {(p.user_name || 'U')[0].toUpperCase()}
                  </span>
                </div>
              )}
              <div className="absolute bottom-2 left-2 bg-black/60 px-2 py-0.5 rounded text-xs text-white">
                {p.user_name || 'Participant'} {p.local && '(You)'}
              </div>
              {isRecording && p.local && (
                <div className="absolute top-2 right-2 flex items-center gap-1 bg-red-600 px-2 py-0.5 rounded text-xs text-white">
                  <Circle className="w-2 h-2 fill-current animate-pulse" />
                  REC
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Controls Bar */}
      <div className="flex items-center justify-center gap-3 py-4 px-6 bg-gray-800/80" data-testid="video-call-controls">
        <button
          onClick={toggleMic}
          className={`p-3 rounded-full transition-colors ${isMuted ? 'bg-red-500 text-white' : 'bg-gray-700 text-white hover:bg-gray-600'}`}
          data-testid="toggle-mic"
        >
          {isMuted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
        </button>

        <button
          onClick={toggleCam}
          className={`p-3 rounded-full transition-colors ${isCamOff ? 'bg-red-500 text-white' : 'bg-gray-700 text-white hover:bg-gray-600'}`}
          data-testid="toggle-cam"
        >
          {isCamOff ? <CameraOff className="w-5 h-5" /> : <Camera className="w-5 h-5" />}
        </button>

        <button
          onClick={toggleScreenShare}
          className="p-3 rounded-full bg-gray-700 text-white hover:bg-gray-600 transition-colors"
          data-testid="screen-share"
        >
          <Monitor className="w-5 h-5" />
        </button>

        <button
          onClick={isRecording ? stopRecording : startRecording}
          className={`p-3 rounded-full transition-colors ${isRecording ? 'bg-red-500 text-white animate-pulse' : 'bg-gray-700 text-white hover:bg-gray-600'}`}
          data-testid="toggle-recording"
        >
          <Circle className={`w-5 h-5 ${isRecording ? 'fill-current' : ''}`} />
        </button>

        <button
          onClick={leaveCall}
          className="p-3 rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors ml-4"
          data-testid="leave-call"
        >
          <PhoneOff className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default VideoCallRoom;
